/**
 * Inventory API Service
 * Frontend service for inventory management operations
 */

import axiosClient from '../api/axiosClient';

// Items API
export const itemsApi = {
  // Get items with filtering and pagination
  getItems: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.query) queryParams.append('query', params.query);
    if (params.category) queryParams.append('category', params.category);
    if (params.locationId) queryParams.append('locationId', params.locationId);
    if (params.lowStock) queryParams.append('lowStock', params.lowStock);
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    
    const response = await axiosClient.get(`/inventory/items?${queryParams}`);
    return response.data;
  },

  // Create a new item
  createItem: async (itemData) => {
    const response = await axiosClient.post('/inventory/items', itemData);
    return response.data;
  },

  // Get a specific item
  getItem: async (id) => {
    const response = await axiosClient.get(`/inventory/items/${id}`);
    return response.data;
  },

  // Update an item
  updateItem: async (id, updateData) => {
    const response = await axiosClient.patch(`/inventory/items/${id}`, updateData);
    return response.data;
  },

  // Archive an item (soft delete)
  archiveItem: async (id) => {
    const response = await axiosClient.patch(`/inventory/items/${id}/archive`);
    return response.data;
  },

  // Change item status (draft, active, archived)
  changeItemStatus: async (id, status) => {
    const response = await axiosClient.patch(`/inventory/items/${id}/status`, { status });
    return response.data;
  },

  // Delete an item (hard delete)
  deleteItem: async (id) => {
    const response = await axiosClient.delete(`/inventory/items/${id}`);
    return response.data;
  }
};

// Locations API
export const locationsApi = {
  // Get locations
  getLocations: async () => {
    const response = await axiosClient.get('/inventory/locations');
    return response.data;
  }
};

export const categoriesApi = {
  // Get categories
  getCategories: async () => {
    const response = await axiosClient.get('/inventory/categories');
    return response.data;
  }
};

export const unitsApi = {
  // Get units
  getUnits: async () => {
    const response = await axiosClient.get('/inventory/units');
    return response.data;
  }
};

// Suppliers API
export const suppliersApi = {
  // Get suppliers
  getSuppliers: async () => {
    const response = await axiosClient.get('/inventory/suppliers');
    return response.data;
  },

  // Create a new supplier
  createSupplier: async (supplierData) => {
    const response = await axiosClient.post('/inventory/suppliers', supplierData);
    return response.data;
  },

  // Update a supplier
  updateSupplier: async (id, updateData) => {
    const response = await axiosClient.patch(`/inventory/suppliers/${id}`, updateData);
    return response.data;
  },

  // Delete a supplier
  deleteSupplier: async (id) => {
    const response = await axiosClient.delete(`/inventory/suppliers/${id}`);
    return response.data;
  },

  // Archive a supplier (soft delete)
  archiveSupplier: async (id) => {
    const response = await axiosClient.patch(`/inventory/suppliers/${id}/archive`);
    return response.data;
  }
};

// Stock API
export const stockApi = {
  // Get stock information
  getStock: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.itemId) queryParams.append('itemId', params.itemId);
    if (params.locationId) queryParams.append('locationId', params.locationId);
    
    const response = await axiosClient.get(`/inventory/stock?${queryParams}`);
    return response.data;
  },

  // Update stock thresholds
  updateStockThresholds: async (locationStockId, thresholds) => {
    const response = await axiosClient.patch(`/inventory/stock/${locationStockId}`, thresholds);
    return response.data;
  }
};

// Movement API
export const movementsApi = {
  // Get adjustments with filtering
  getAdjustments: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.range) queryParams.append('range', params.range);
    if (params.itemId) queryParams.append('itemId', params.itemId);
    if (params.locationId) queryParams.append('locationId', params.locationId);
    if (params.reason) queryParams.append('reason', params.reason);
    if (params.page) queryParams.append('page', params.page);
    
    const response = await axiosClient.get(`/inventory/movements/adjustments?${queryParams}`);
    return response.data;
  },

  // Create adjustment
  createAdjustment: async (adjustmentData) => {
    const response = await axiosClient.post('/inventory/adjustments', adjustmentData);
    return response.data;
  },

  // Get transfers with filtering
  getTransfers: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.status) queryParams.append('status', params.status);
    if (params.range) queryParams.append('range', params.range);
    if (params.page) queryParams.append('page', params.page);
    
    const response = await axiosClient.get(`/inventory/movements/transfers?${queryParams}`);
    return response.data;
  },

  // Create receive
  createReceive: async (receiveData) => {
    const response = await axiosClient.post('/inventory/receive', receiveData);
    return response.data;
  },

  // Create transfer
  createTransfer: async (transferData) => {
    const response = await axiosClient.post('/inventory/transfers', transferData);
    return response.data;
  },

  // Update transfer status
  updateTransferStatus: async (transferId, status) => {
    const response = await axiosClient.patch(`/inventory/transfers/${transferId}`, { status });
    return response.data;
  },

  // Get usage summary (for dashboard)
  getUsageSummary: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.range) queryParams.append('range', params.range);
    if (params.itemId) queryParams.append('itemId', params.itemId);
    
    const response = await axiosClient.get(`/inventory/movements/usage-summary?${queryParams}`);
    return response.data;
  },

  // Get incoming transfers (for dashboard)
  getIncomingTransfers: async () => {
    const response = await axiosClient.get('/inventory/movements/transfers?status=sent');
    return response.data;
  }
};

// Analytics API
export const analyticsApi = {
  // GET /api/inventory/analytics?range=day|week|month
  getAnalytics: async (range = 'month') => {
    const queryParams = new URLSearchParams();
    if (range) queryParams.append('range', range);
    const response = await axiosClient.get(`/inventory/analytics?${queryParams}`);
    return response.data;
  }
};

// Settings API
export const settingsApi = {
  // Get inventory settings
  getSettings: async () => {
    const response = await axiosClient.get('/inventory/settings');
    return response.data;
  },

  // Update inventory settings
  updateSettings: async (settings) => {
    const response = await axiosClient.put('/inventory/settings', settings);
    return response.data;
  }
};

// Alerts API
export const alertsApi = {
  // Get low stock items
  getLowStock: async (config = {}) => {
    const response = await axiosClient.get('/inventory/low-stock', config);
    return response.data;
  },

  // Test alerts
  testAlerts: async () => {
    const response = await axiosClient.post('/inventory/alerts/test');
    return response.data;
  }
};

// Combined API object
const inventoryApi = {
  items: itemsApi,
  suppliers: suppliersApi,
  locations: locationsApi,
  categories: categoriesApi,
  units: unitsApi,
  stock: stockApi,
  movements: movementsApi,
  analytics: analyticsApi,
  settings: settingsApi,
  alerts: alertsApi
};

export default inventoryApi;
